import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Home,
  Info,
  Activity,
  PhoneCall,
  LogIn,
  Menu,
  X,
  Plus,
  Clock,
  ShieldCheck,
  ChevronRight,
  Sparkles,
  HeartPulse
} from 'lucide-react';

const navLinks = [
  { name: 'Home', path: '/', icon: Home },
  { name: 'About', path: '/about', icon: Info },
  { name: 'Services', path: '/services', icon: Activity },
  { name: 'Contact', path: '/contact', icon: PhoneCall },
];

const PublicNavbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Detect scroll to adjust navbar elevation & backdrop blur
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 15) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className="sticky top-0 z-50 w-full font-sans">
      {/* Top Announcement Bar */}
      <div className="bg-gradient-to-r from-emerald-950 via-teal-950 to-slate-950 text-emerald-100 text-xs py-2 px-4 hidden sm:block border-b border-emerald-800/30">
        <div className="max-w-7xl mx-auto flex justify-between items-center tracking-wide">
          <div className="flex items-center gap-4">
            <span className="inline-flex items-center gap-2 font-semibold text-emerald-300">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
              </span>
              <span>Open 24/7 for Medical Emergencies</span>
            </span>
            <span className="text-emerald-700">•</span>
            <span className="hidden md:inline-flex items-center gap-1.5 text-slate-300 font-medium">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
              <span>100% Genuine Medicines & Healthcare Supplies</span>
            </span>
          </div>
          
          <div className="flex items-center gap-4 text-slate-300 text-[11px] font-medium">
            <span className="flex items-center gap-1.5 hover:text-emerald-300 transition-colors">
              <HeartPulse className="w-3.5 h-3.5 text-rose-400 animate-pulse" />
              <span>Instant Store Pickup</span>
            </span>
            <span className="text-slate-600">|</span>
            <span className="flex items-center gap-1.5 hover:text-emerald-300 transition-colors">
              <Clock className="w-3.5 h-3.5 text-teal-400" />
              <span>Express Delivery Available</span>
            </span>
          </div>
        </div>
      </div>

      {/* Main Navigation Bar */}
      <nav
        className={`transition-all duration-300 ${
          scrolled
            ? 'bg-white/95 backdrop-blur-xl shadow-[0_12px_40px_-10px_rgba(16,185,129,0.15)] border-b border-emerald-100 py-3'
            : 'bg-white/90 backdrop-blur-md border-b border-slate-100 shadow-sm py-4'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            
            {/* Brand Logo & Title */}
            <Link
              to="/"
              className="group flex items-center gap-3.5 select-none"
            >
              {/* Animated Glowing Logo Icon */}
              <div className="relative flex items-center justify-center">
                <div className="absolute -inset-1.5 bg-gradient-to-r from-emerald-500 via-teal-400 to-cyan-500 rounded-2xl blur-md opacity-50 group-hover:opacity-100 transition duration-500 group-hover:scale-105"></div>
                <div className="relative w-11 h-11 sm:w-12 sm:h-12 bg-gradient-to-tr from-emerald-600 via-teal-600 to-cyan-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-emerald-600/30 group-hover:scale-105 transition-transform duration-300">
                  <div className="relative flex items-center justify-center">
                    <Plus className="w-6 h-6 sm:w-7 sm:h-7 text-white stroke-[3] transform group-hover:rotate-90 transition-transform duration-500" />
                    <Sparkles className="w-3.5 h-3.5 text-emerald-200 absolute -top-1.5 -right-1.5 opacity-90 animate-pulse" />
                  </div>
                </div>
              </div>

              {/* Brand Text */}
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-black tracking-tight bg-gradient-to-r from-slate-900 via-emerald-950 to-teal-900 bg-clip-text text-transparent group-hover:to-emerald-600 transition-colors">
                    Sai Pharmacy
                  </span>
                  <span className="hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-gradient-to-r from-emerald-50 to-teal-50 text-emerald-700 border border-emerald-200/80 shadow-2xs">
                    Plus
                  </span>
                </div>
                <span className="text-[11px] font-bold tracking-widest text-emerald-600 uppercase -mt-0.5">
                  Trusted Healthcare Partner
                </span>
              </div>
            </Link>

            {/* Desktop Navigation Links Pill Container */}
            <div className="hidden md:flex items-center gap-1 p-1.5 bg-slate-100/90 backdrop-blur-md rounded-full border border-slate-200/80 shadow-inner">
              {navLinks.map((link) => {
                const isActive = location.pathname === link.path;
                const IconComponent = link.icon;
                return (
                  <Link
                    key={link.name}
                    to={link.path}
                    className={`relative flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold transition-all duration-300 ${
                      isActive
                        ? 'text-emerald-800 bg-white shadow-md shadow-emerald-500/10 ring-1 ring-slate-200'
                        : 'text-slate-600 hover:text-emerald-700 hover:bg-white/80'
                    }`}
                  >
                    <IconComponent
                      className={`w-4 h-4 transition-colors ${
                        isActive ? 'text-emerald-600 stroke-[2.5]' : 'text-slate-400 group-hover:text-emerald-600'
                      }`}
                    />
                    <span>{link.name}</span>
                    {isActive && (
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping absolute top-2 right-2" />
                    )}
                  </Link>
                );
              })}
            </div>

            {/* Desktop Right CTA Portal Button */}
            <div className="hidden md:flex items-center gap-3">
              <Link
                to="/login"
                className="group relative inline-flex items-center justify-center gap-2.5 px-6 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider text-white overflow-hidden shadow-lg shadow-emerald-600/25 hover:shadow-emerald-600/40 active:scale-95 transition-all duration-300 bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 hover:from-emerald-500 hover:to-cyan-500"
              >
                {/* Shiny sweep effect */}
                <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out bg-gradient-to-r from-transparent via-white/25 to-transparent"></div>
                <LogIn className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
                <span>Portal Login</span>
                <ChevronRight className="w-3.5 h-3.5 opacity-70 group-hover:translate-x-1 group-hover:opacity-100 transition-all" />
              </Link>
            </div>

            {/* Mobile Menu Hamburger Button */}
            <div className="md:hidden flex items-center gap-2.5">
              <Link
                to="/login"
                className="px-3.5 py-1.5 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-emerald-600 to-teal-600 shadow-md shadow-emerald-600/20 active:scale-95"
              >
                Login
              </Link>
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="p-2.5 rounded-xl text-slate-700 hover:text-emerald-600 hover:bg-emerald-50 border border-slate-200 transition-all focus:outline-none"
                aria-label="Toggle Navigation Menu"
              >
                {isOpen ? (
                  <X className="w-6 h-6 text-emerald-600 transform rotate-90 transition-transform duration-200" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Dropdown Navigation Drawer */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${
            isOpen ? 'max-h-96 opacity-100 mt-3' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="mx-4 p-3.5 bg-white/95 backdrop-blur-xl rounded-2xl border border-emerald-100 shadow-2xl shadow-emerald-500/10 space-y-1.5">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.path;
              const IconComponent = link.icon;
              return (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`flex items-center justify-between px-4 py-3 rounded-xl text-xs font-bold transition-all ${
                    isActive
                      ? 'text-emerald-800 bg-emerald-50 border border-emerald-200/80 shadow-xs'
                      : 'text-slate-700 hover:text-emerald-700 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`p-2 rounded-lg ${
                        isActive ? 'bg-gradient-to-tr from-emerald-600 to-teal-600 text-white' : 'bg-slate-100 text-slate-500'
                      }`}
                    >
                      <IconComponent className="w-4 h-4" />
                    </div>
                    <span>{link.name}</span>
                  </div>
                  {isActive ? (
                    <span className="w-2 h-2 rounded-full bg-emerald-600"></span>
                  ) : (
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  )}
                </Link>
              );
            })}

            <div className="pt-2 border-t border-slate-100">
              <Link
                to="/login"
                onClick={() => setIsOpen(false)}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl text-xs font-bold uppercase tracking-wider text-white bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 shadow-lg shadow-emerald-600/25 active:scale-98"
              >
                <LogIn className="w-4 h-4" />
                <span>Sign in to Store</span>
              </Link>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default PublicNavbar;
